import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Divider,
  Button,
  CircularProgress,
} from "@mui/material";
import { useAppSelector } from "@/store";
import * as articlesApi from "@/api/articles.api";
import { LoadingScreen } from "@/components/LoadingScreen";
import { EmptyState } from "@/components/EmptyState";
import {
  documentTitleCategory,
  CATEGORY_INFINITE_SCROLL_MAX_PAGES,
} from "@/constants/app";
import { hr } from "@/constants/strings.hr";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import type { Article } from "@/types";
import { ArticleListItem } from "./ArticleListItem";

const PAGE_SIZE = 20;

export function CategoryPage() {
  const { categoryName } = useParams<{ categoryName: string }>();
  const categories = useAppSelector((s) => s.categories.items);
  const [items, setItems] = useState<Article[]>([]);
  const [total, setTotal] = useState(0);
  const [pagesFetched, setPagesFetched] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const nextPageToLoad = useRef(1);
  const loadLock = useRef(false);
  const itemsLenRef = useRef(0);
  const totalRef = useRef(0);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const category = categories.find(
    (c) => c.name.toLowerCase() === categoryName?.toLowerCase(),
  );

  useDocumentTitle(category ? documentTitleCategory(category.name) : null);

  const hasMore = total > 0 && items.length < total;

  const loadPage = useCallback(
    async (page: number, append: boolean) => {
      if (!category) return;
      if (loadLock.current) return;
      loadLock.current = true;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const data = await articlesApi.getArticles({
          categoryId: category.id,
          page,
          limit: PAGE_SIZE,
        });
        if (append) {
          setItems((prev) => [...prev, ...data.items]);
        } else {
          setItems(data.items);
        }
        setTotal(data.total);
        setPagesFetched(page);
        nextPageToLoad.current = page + 1;
      } finally {
        loadLock.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [category],
  );

  useLayoutEffect(() => {
    itemsLenRef.current = items.length;
    totalRef.current = total;
  }, [items.length, total]);

  useEffect(() => {
    if (!category) return;
    setItems([]);
    setTotal(0);
    setPagesFetched(0);
    nextPageToLoad.current = 1;
    loadPage(1, false);
  }, [category?.id, loadPage]);

  const tryLoadNext = useCallback(() => {
    if (!category) return;
    if (loadLock.current) return;
    if (itemsLenRef.current >= totalRef.current) return;
    const next = nextPageToLoad.current;
    if (next < 1) return;
    loadPage(next, true);
  }, [category, loadPage]);

  useEffect(() => {
    if (!category) return;
    if (!hasMore) return;
    if (pagesFetched >= CATEGORY_INFINITE_SCROLL_MAX_PAGES) return;
    if (loading) return;

    const el = sentinelRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        if (loadLock.current) return;
        if (itemsLenRef.current >= totalRef.current) return;
        tryLoadNext();
      },
      { root: null, rootMargin: "240px", threshold: 0 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [category, hasMore, pagesFetched, loading, tryLoadNext, items.length]);

  if (!category) {
    return <EmptyState title={hr.category.notFound} />;
  }

  if (loading && items.length === 0) {
    return <LoadingScreen />;
  }

  const useInfinite =
    hasMore && pagesFetched < CATEGORY_INFINITE_SCROLL_MAX_PAGES;
  const useButton =
    hasMore && pagesFetched >= CATEGORY_INFINITE_SCROLL_MAX_PAGES;

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        {category.name}
      </Typography>
      {category.description && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            mb: 1,
          }}
        >
          {category.description}
        </Typography>
      )}
      <Divider sx={{ mb: 2 }} />
      {items.length > 0 ? (
        <>
          {items.map((article) => (
            <ArticleListItem key={article.id} article={article} />
          ))}

          {useInfinite && (
            <Box ref={sentinelRef} aria-hidden sx={{ height: 8, my: 1 }} />
          )}

          {useInfinite && loadingMore && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {useButton && (
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 1 }}
            >
              <Button
                variant="outlined"
                onClick={() => tryLoadNext()}
                disabled={loadingMore}
              >
                {loadingMore ? (
                  <CircularProgress size={22} />
                ) : (
                  hr.category.loadMore
                )}
              </Button>
            </Box>
          )}
        </>
      ) : (
        <EmptyState
          title={hr.category.empty}
          titleVariant="body1"
          sx={{ py: 6 }}
        />
      )}
    </Box>
  );
}
